/**
 * Simplified Frontend API service for student attendance tracking
 */
import { getDateRange } from '../utils/timezoneUtils';
import { BACKEND_API_BASE_URL } from '../config';

const API_BASE_URL = `${BACKEND_API_BASE_URL}/api`;

class StudentTrackingAPI {
  
  /**
   * Get daily attendance summary for a student with chart data
   */
  async getStudentDailyAttendance(studentId, startDate, endDate, token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/student-tracking/${studentId}/attendance/daily?startDate=${startDate}&endDate=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch student attendance data');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching student daily attendance:', error);
      throw error;
    }
  }

  /**
   * Get recent attendance summary (last 30 days)
   */
  async getStudentRecentAttendance(studentId, token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/student-tracking/${studentId}/attendance/recent`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch recent attendance data');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching recent attendance:', error);
      throw error;
    }
  }

  /**
   * Helper method to format date for API calls
   */
  formatDate(date) {
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
  }

  /**
   * Get recent notes for a student (limit 10 most recent)
   */
  async getStudentNotes(studentId, token) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/student-tracking/${studentId}/notes?limit=10`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch student notes');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching student notes:', error);
      throw error;
    }
  }

  /**
   * Get predefined date ranges using timezone-aware utility
   */
  getDateRanges() {
    return {
      last7Days: getDateRange('last7Days'),
      last30Days: getDateRange('last30Days'),
      last120Days: getDateRange('last120Days'),
      lastWeek: getDateRange('lastWeek'),
      lastMonth: getDateRange('lastMonth'),
      last3Months: getDateRange('last3Months')
    };
  }
}

export const studentTrackingAPI = new StudentTrackingAPI();
